import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { INTENT_LABELS } from "@/lib/first-steps-copy";
import type { PlatformIntent } from "@/lib/platform-intents";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_authenticated/admin/install")({
  head: () => ({
    meta: [
      { title: "Приложение на устройствах — админка TurkeyDrive" },
      { name: "description", content: "Сколько резидентов поставили TurkeyDrive на домашний экран." },
      { property: "og:title", content: "Приложение на устройствах — админка TurkeyDrive" },
      { property: "og:description", content: "Сколько резидентов поставили TurkeyDrive на домашний экран." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: AdminInstallPage,
});

type Bucket = "installed" | "browser" | "never";
const TABS: { key: Bucket; label: string }[] = [
  { key: "installed", label: "Поставили на экран" },
  { key: "browser", label: "Через браузер" },
  { key: "never", label: "Ни разу не заходили" },
];
const PLATFORM: Record<string, string> = { ios: "iPhone", android: "Android", desktop: "Компьютер" };
const fmt = (d: string | null) =>
  d ? new Date(d).toLocaleDateString("ru-RU", { day: "numeric", month: "long", year: "numeric" }) : null;

function AdminInstallPage() {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const [bucket, setBucket] = useState<Bucket>("installed");

  useEffect(() => {
    (async () => {
      const { data: userRes } = await supabase.auth.getUser();
      const uid = userRes.user?.id;
      if (!uid) return navigate({ to: "/profile" });
      const { data: prof } = await supabase.from("profiles").select("is_admin").eq("user_id", uid).maybeSingle();
      if (!prof?.is_admin) return navigate({ to: "/profile" });
      setAuthorized(true);
    })();
  }, [navigate]);

  const { data: stats } = useQuery({
    queryKey: ["admin", "install-stats"],
    enabled: authorized,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("app_install_stats");
      if (error) throw error;
      return (Array.isArray(data) ? data[0] : data) ?? null;
    },
  });

  const { data: people = [], isLoading: peopleLoading } = useQuery({
    queryKey: ["admin", "install-people", bucket],
    enabled: authorized,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("app_install_people", { p_bucket: bucket });
      if (error) throw error;
      return data ?? [];
    },
  });

  if (!authorized) {
    return <div className="mx-auto max-w-5xl px-4 py-8 text-sm text-td-muted">Проверка доступа…</div>;
  }

  const tiles = stats
    ? [
        { label: "Поставили на экран", value: stats.installed },
        { label: "Из них заходили за неделю", value: stats.installed_active_7d },
        { label: "Заходят через браузер", value: stats.browser_only },
        { label: "Ни разу не заходили", value: Math.max(0, stats.residents - stats.seen) },
        { label: "Пуши включены", value: stats.push_enabled },
      ]
    : [];

  const copy = async () => {
    const text = people.map((p) => p.full_name).filter(Boolean).join("\n");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Список скопирован");
    } catch {
      toast.error("Не удалось скопировать");
    }
  };

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <button type="button" onClick={() => navigate({ to: "/profile" })} className="mb-4 text-sm text-td-muted hover:text-td-dark">
        ← Назад
      </button>
      <h1 className="mb-4 text-2xl font-semibold text-td-dark">Приложение на устройствах</h1>

      {!stats ? (
        <div className="text-sm text-td-muted">Загружаем…</div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {tiles.map((t) => (
              <div key={t.label} className="flex min-w-0 flex-col rounded-2xl border border-td-line bg-card p-3">
                <div className="break-words text-xs text-td-muted">{t.label}</div>
                <div className="mt-auto pt-1 text-2xl font-semibold text-td-dark">{t.value}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-xs text-td-muted">
            iPhone {stats.ios} · Android {stats.android} · Компьютер {stats.desktop}
          </p>
          {stats.installed_idle_14d > 0 ? (
            <div className="mt-4 rounded-2xl border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm font-semibold text-destructive">
              Поставили приложение, но не заходили больше двух недель: {stats.installed_idle_14d}
            </div>
          ) : null}
        </>
      )}

      <IntentStats enabled={authorized} />

      <div className="mt-6 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setBucket(t.key)}
            className={`rounded-full border px-3 py-1.5 text-sm transition ${
              bucket === t.key ? "border-td-dark bg-td-dark text-primary-foreground" : "border-td-line bg-card text-td-dark hover:bg-td-soft/50"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-3 flex items-center justify-between">
        <span className="text-sm text-td-muted">{peopleLoading ? "Загружаем…" : `Человек: ${people.length}`}</span>
        <Button variant="outline" size="sm" onClick={copy} disabled={!people.length}>
          Скопировать список
        </Button>
      </div>

      <ul className="mt-3 divide-y divide-td-line rounded-2xl border border-td-line bg-card">
        {people.map((p) => {
          const last = fmt(p.last_visit_at);
          const meta = [p.city, last ? `заходил(а) ${last}` : null, p.last_platform ? PLATFORM[p.last_platform] ?? null : null]
            .filter(Boolean)
            .join(" · ");
          return (
            <li key={p.user_id} className="flex min-w-0 items-center gap-3 px-4 py-3">
              {p.photo_url ? (
                <img loading="lazy" decoding="async"
                  src={p.photo_url}
                  alt={p.full_name || ""}
                  className="size-9 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-td-soft text-sm font-semibold text-td-dark">
                  {(p.full_name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <Link
                  to="/people/$userId"
                  params={{ userId: p.user_id }}
                  className="block truncate text-sm font-medium text-td-dark hover:underline"
                >
                  {p.full_name || "Без имени"}
                </Link>
                {meta ? <div className="truncate text-xs text-td-muted">{meta}</div> : null}
              </div>
              {p.push_enabled ? (
                <span className="shrink-0 rounded-full bg-td-soft px-2 py-0.5 text-[11px] text-td-dark">пуши включены</span>
              ) : null}
            </li>
          );
        })}
        {!peopleLoading && !people.length ? <li className="px-4 py-6 text-center text-sm text-td-muted">Пока никого</li> : null}
      </ul>

      <p className="mt-6 text-xs text-td-muted">
        Цифры начинают накапливаться с момента публикации: приложение отмечает только тех, кто зашёл после неё.
      </p>
    </main>
  );
}

/** Блок «Намерения»: только чтение. */
function IntentStats({ enabled }: { enabled: boolean }) {
  const { data = [] } = useQuery({
    queryKey: ["admin-intent-stats"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_intent_stats");
      if (error) throw error;
      return data ?? [];
    },
  });
  const { data: er } = useQuery({
    queryKey: ["admin-expert-event-stats"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_expert_event_stats");
      if (error) throw error;
      return data?.[0] ?? null;
    },
  });
  const { data: ce } = useQuery({
    queryKey: ["admin-circle-expert-stats"],
    enabled,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("admin_circle_expert_stats");
      if (error) throw error;
      return data?.[0] ?? null;
    },
  });
  if (!data.length) return null;
  const ceTiles = ce
    ? [
        { label: "Активных активистов сообществ", value: ce.active_experts },
        { label: "Заявок в активисты в ожидании", value: ce.pending_requests },
      ]
    : [];
  const erTiles = er
    ? [
        { label: "Открытых запросов экспертов", value: er.open_requests },
        { label: "Предложений всего", value: er.offers_total },
        { label: "Предложений принято", value: er.offers_accepted },
        { label: "Организаторов для экспертов", value: er.hosts_enabled },
      ]
    : [];
  return (
    <section className="mt-6">
      <h2 className="mb-2 text-lg font-semibold text-td-dark">Намерения</h2>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {data.map((r) => (
          <div key={r.intent} className="flex min-w-0 flex-col rounded-2xl border border-td-line bg-card p-3">
            <div className="break-words text-xs text-td-muted">
              {INTENT_LABELS[r.intent as PlatformIntent] ?? r.intent}
            </div>
            <div className="mt-auto pt-1 text-2xl font-semibold text-td-dark">{r.people}</div>
            {r.did_action != null && (
              <div className="text-xs text-td-muted">
                {r.intent === "organize" ? "опубликовали событие" : r.intent === "community" ? "ведут сообщество" : "заполнили карточку"}: {r.did_action}
              </div>
            )}
          </div>
        ))}
        {[...erTiles, ...ceTiles].map((t) => (
          <div key={t.label} className="flex min-w-0 flex-col rounded-2xl border border-td-line bg-card p-3">
            <div className="break-words text-xs text-td-muted">{t.label}</div>
            <div className="mt-auto pt-1 text-2xl font-semibold text-td-dark">{t.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
}
